import {
  BlankTransport,
  type ILogLayer,
  LogLayer,
  type LogLayerTransport,
} from "@-/com.npmjs.loglayer";
import type { LogLayerTransportParams } from "@-/com.npmjs.loglayer__transport";
import { isErrorLike, serializeError } from "@-/com.npmjs.serialize-error";
import { hasLength, isNonEmpty } from "@-/com.npmjs.ts-array-length";
import { createAppLogListenerTransport } from "#app-log-listeners";

/**
 * The subset of consola log levels LogLayer supports directly. Consola numeric
 * levels are: 0=error/fatal, 1=warn, 2=log, 3=info, 4=debug, 5=trace.
 */
export type AppLogLevel = "trace" | "debug" | "info" | "warn" | "error" | "fatal";

function copyOwnProperties(value: object): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of Object.getOwnPropertyNames(value)) {
    result[key] = Reflect.get(value, key);
  }
  for (const [key, propertyValue] of Object.entries(value)) {
    result[key] = propertyValue;
  }
  return result;
}

export function serializeAppError(error: unknown): Record<string, unknown> {
  const serialized = serializeError(error);
  return copyOwnProperties(serialized);
}

function normalizeConsoleValue(value: unknown): unknown {
  if (value instanceof Error || isErrorLike(value)) {
    return serializeAppError(value);
  }
  if (typeof value !== "object" || value === null) {
    return value;
  }
  return copyOwnProperties(value);
}

function stringifyConsoleValue(value: unknown): string {
  try {
    return JSON.stringify(normalizeConsoleValue(value));
  } catch {
    return Object.prototype.toString.call(value);
  }
}

export function formatConsoleTransportMessages(params: LogLayerTransportParams): unknown[] {
  const messages = isNonEmpty(params.messages) ? params.messages : [];
  const formattedMessages =
    !params.hasData || params.data === undefined || params.data === null
      ? messages
      : hasLength(messages, 0)
        ? [stringifyConsoleValue(params.data)]
        : [...messages, stringifyConsoleValue(params.data)];

  return appendConsoleLogDelta(formattedMessages);
}

const CONSOLE_LOG_DELTA_PATTERN = /^\+\d+ms$/;

let previousConsoleLogAt: number | undefined;

function readNowMs(): number {
  return typeof performance === "object" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

function nextConsoleLogDelta(): string {
  const now = readNowMs();
  const previous = previousConsoleLogAt;
  previousConsoleLogAt = now;
  return `+${previous === undefined ? 0 : Math.max(0, Math.round(now - previous))}ms`;
}

function hasConsoleLogDelta(messages: readonly unknown[]): boolean {
  const last = messages.at(-1);
  return typeof last === "string" && CONSOLE_LOG_DELTA_PATTERN.test(last);
}

export function appendConsoleLogDelta(messages: readonly unknown[]): unknown[] {
  if (hasConsoleLogDelta(messages)) {
    return [...messages];
  }
  return [...messages, nextConsoleLogDelta()];
}

export function createConsoleTransport(
  logger: Console,
  nodeEnv: string | undefined = process.env.NODE_ENV,
): LogLayerTransport {
  return new BlankTransport({
    enabled: isConsoleTransportEnabled(nodeEnv),
    shipToLogger(params) {
      const messages = formatConsoleTransportMessages(params);
      switch (params.logLevel) {
        case "trace":
        case "debug": {
          logger.debug(...messages);
          break;
        }
        case "info": {
          logger.info(...messages);
          break;
        }
        case "warn": {
          logger.warn(...messages);
          break;
        }
        case "error":
        case "fatal": {
          logger.error(...messages);
          break;
        }
      }
      return messages;
    },
  });
}

/**
 * Console output is a development-only sink. Production logs continue through
 * the runtime's remote transports (Sentry on clients and Axiom on the server),
 * but must never be written to the browser, device, or worker console.
 */
export function isConsoleTransportEnabled(
  nodeEnv: string | undefined = process.env.NODE_ENV,
): boolean {
  return nodeEnv !== "production";
}

export function createEnabledConsoleTransports(
  logger: Console,
  nodeEnv: string | undefined = process.env.NODE_ENV,
): LogLayerTransport[] {
  return isConsoleTransportEnabled(nodeEnv) ? [createConsoleTransport(logger, nodeEnv)] : [];
}

/**
 * Build the shared app LogLayer instance from a runtime-specific set of
 * transports. Every runtime's LogLayer flows through here so the error
 * serialization contract is identical: errors passed via `withError(...)` are
 * serialized with `serialize-error` (per the LogLayer error-handling docs)
 * before reaching any transport, so both the console output and the Axiom/Sentry
 * sinks see a plain, structured error object rather than a bare `Error`.
 */
export function buildAppLogLayer(transports: LogLayerTransport | LogLayerTransport[]): ILogLayer {
  // The listener transport is always installed: it is a no-op until the diagnostics recorder
  // subscribes, and having it here means every runtime's LogLayer can feed the recorder.
  return new LogLayer({
    errorSerializer: serializeAppError,
    transport: [
      ...(Array.isArray(transports) ? transports : [transports]),
      createAppLogListenerTransport(),
    ],
  });
}

const CONSOLA_TYPE_TO_APP_LOG_LEVEL: Record<string, AppLogLevel> = {
  box: "info",
  critical: "fatal",
  debug: "debug",
  error: "error",
  fail: "error",
  fatal: "fatal",
  info: "info",
  log: "info",
  notice: "info",
  ready: "info",
  silent: "trace",
  start: "info",
  success: "info",
  trace: "trace",
  verbose: "debug",
  warn: "warn",
};

const CONSOLA_NUMERIC_LEVEL_TO_APP_LOG_LEVEL: Record<number, AppLogLevel> = {
  0: "fatal",
  1: "warn",
  2: "info",
  3: "info",
  4: "debug",
  5: "trace",
};

/**
 * Map a consola log object (`{ type, level }`) onto the LogLayer level to emit.
 * Prefers the semantic `type` (e.g. `success`, `fail`) and falls back to the
 * numeric level, mirroring the previous consola→Sentry bridge so level fidelity
 * is preserved when the reporter now forwards into LogLayer.
 */
export function consolaLogLevelToAppLogLevel(logObject: {
  level: number;
  type: string;
}): AppLogLevel {
  return (
    CONSOLA_TYPE_TO_APP_LOG_LEVEL[logObject.type] ??
    CONSOLA_NUMERIC_LEVEL_TO_APP_LOG_LEVEL[logObject.level] ??
    "info"
  );
}
